import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Pencil, Archive, Trash2, ArchiveRestore, Loader2, Search, X, ListChecks, Lock, GripVertical,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import { refreshTaskOptions } from '../../utils/taskOptions';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Keep in sync with backend EDITABLE_LIST_TYPES + LOCKED_KEY_LIST_TYPES.
const LIST_TYPES = [
  { key: 'categories', label: 'Categories', singular: 'Category', description: 'Task categories (Technical SEO, On-Page, Off-Page, UI/UX Design, …). Drive the primary Category filter across every task view.', supportsColor: true, hasCanonicalKey: false },
  { key: 'content_types', label: 'Content Types', singular: 'Content Type', description: 'Content deliverable types (Blog Post, Video, Infographic, …).', supportsColor: false, hasCanonicalKey: false },
  { key: 'task_statuses', label: 'Task Statuses', singular: 'Task Status', description: 'Workflow statuses tasks move through. Rename freely — dashboards & filters use immutable canonical keys.', supportsColor: true, hasCanonicalKey: true },
  { key: 'developer_statuses', label: 'Developer Statuses', singular: 'Developer Status', description: 'Developer-workflow states (Yes, No, Pending, In Progress, Completed). Canonical keys are immutable.', supportsColor: true, hasCanonicalKey: true },
  { key: 'uiux_statuses', label: 'UI/UX Statuses', singular: 'UI/UX Status', description: 'UI/UX workflow states. Canonical keys are immutable.', supportsColor: true, hasCanonicalKey: true },
  { key: 'manager_approval_statuses', label: 'Approval Statuses', singular: 'Approval Status', description: 'Manager-approval workflow states (Pending, Approved, Rejected, Standby, Not Required). Canonical keys are immutable.', supportsColor: true, hasCanonicalKey: true },
  { key: 'dependency_types', label: 'Dependency Types', singular: 'Dependency Type', description: 'Task dependency values used in the Dependency filter. Canonical keys are immutable.', supportsColor: false, hasCanonicalKey: true },
  { key: 'priorities', label: 'Priorities', singular: 'Priority', description: 'Task priority levels (Low, Medium, High, Critical).', supportsColor: true, hasCanonicalKey: false },
  { key: 'tags', label: 'Tags', singular: 'Tag', description: 'Free-form tags admins can attach to tasks for reporting.', supportsColor: true, hasCanonicalKey: false },
  { key: 'output_types', label: 'Output Types', singular: 'Output Type', description: 'Deliverable types (Article, Design File, Audit Report, …).', supportsColor: false, hasCanonicalKey: false },
];

const emptyForm = {
  name: '',
  description: '',
  color: '',
  icon: '',
  is_active: true,
};

// Draggable table row using @dnd-kit. The drag handle is the leading cell so
// clicking anywhere else (edit / toggle / delete buttons) still works normally.
const SortableOptionRow = ({ item, active, onEdit, onToggle, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    backgroundColor: isDragging ? '#f1f5f9' : undefined,
  };
  return (
    <tr
      ref={setNodeRef}
      style={style}
      data-testid={`task-options-row-${item.id}`}
      className="border-b border-slate-100 hover:bg-slate-50"
    >
      <td className="pl-4 pr-2 py-3 w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-600 touch-none p-1"
          title="Drag to reorder"
          data-testid={`task-options-drag-${item.id}`}
        >
          <GripVertical size={16} />
        </button>
      </td>
      <td className="px-4 py-3 text-sm text-slate-500 font-mono">{item.order}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-2">
          {item.color && (
            <span
              className="w-3 h-3 rounded-full border border-slate-300 flex-shrink-0"
              style={{ backgroundColor: item.color }}
              title={item.color}
            />
          )}
          <span className="font-medium text-slate-900">{item.name}</span>
        </span>
      </td>
      {active.hasCanonicalKey && (
        <td className="px-4 py-3">
          <code className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded font-mono">
            {item.canonical_key || '—'}
          </code>
        </td>
      )}
      <td className="px-4 py-3 text-sm text-slate-600 max-w-md truncate">
        {item.description || <span className="text-slate-300">—</span>}
      </td>
      {active.supportsColor && (
        <td className="px-4 py-3 text-xs text-slate-500 font-mono">
          {item.color || <span className="text-slate-300">—</span>}
        </td>
      )}
      <td className="px-4 py-3">
        {item.is_active ? (
          <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-1 rounded">Active</span>
        ) : (
          <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded">Archived</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => onEdit(item)}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900"
            title="Edit"
            data-testid={`task-options-edit-${item.id}`}
          >
            <Pencil size={15} />
          </button>
          <button
            onClick={() => onToggle(item)}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900"
            title={item.is_active ? 'Archive' : 'Activate'}
            data-testid={`task-options-toggle-${item.id}`}
          >
            {item.is_active ? <Archive size={15} /> : <ArchiveRestore size={15} />}
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-600"
            title={active.hasCanonicalKey ? 'Archive (soft delete)' : 'Delete'}
            data-testid={`task-options-delete-${item.id}`}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </td>
    </tr>
  );
};

const TaskOptions = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(LIST_TYPES[0].key);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const active = LIST_TYPES.find((t) => t.key === activeTab) || LIST_TYPES[0];
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const loadItems = useCallback(async () => {
    if (!token) return;
    const requested = activeTab;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/task-options/admin/options/${requested}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Ignore a stale, slower response from a tab that's no longer active.
      if (requested !== activeTabRef.current) return;
      setItems(res.data.items || []);
    } catch (e) {
      if (requested !== activeTabRef.current) return;
      toast.error(e.response?.data?.detail || `Failed to load ${requested}`);
    } finally {
      if (requested === activeTabRef.current) setLoading(false);
    }
  }, [token, activeTab]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      description: item.description || '',
      color: item.color || '',
      icon: item.icon || '',
      is_active: item.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      name,
      description: form.description.trim() || null,
      color: form.color.trim() || null,
      icon: form.icon.trim() || null,
      is_active: form.is_active,
    };
    try {
      if (editingItem) {
        await axios.put(`${API}/task-options/admin/options/${activeTab}/${editingItem.id}`, payload, authHeader);
        toast.success(`Updated "${name}"`);
      } else {
        await axios.post(`${API}/task-options/admin/options/${activeTab}`, payload, authHeader);
        toast.success(`Created "${name}"`);
      }
      setShowModal(false);
      await loadItems();
      // Invalidate the shared taskOptions cache so every consumer (TaskTable,
      // Kanban, Reports, ...) re-fetches and shows the new/edited value
      // without requiring a page reload.
      await refreshTaskOptions(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(
        `${API}/task-options/admin/options/${activeTab}/${item.id}`,
        { is_active: !item.is_active },
        authHeader,
      );
      toast.success(item.is_active ? `Archived "${item.name}"` : `Activated "${item.name}"`);
      await loadItems();
      await refreshTaskOptions(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (item) => {
    const msg = active.hasCanonicalKey
      ? `Archive "${item.name}"? Because this list has locked canonical keys, deletes are soft — the item is hidden from dropdowns but preserved for historical tasks.`
      : `Delete "${item.name}" permanently? Historical tasks using this value are unaffected but new dropdowns will no longer show it.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/task-options/admin/options/${activeTab}/${item.id}`, authHeader);
      toast.success(active.hasCanonicalKey ? `Archived "${item.name}"` : `Deleted "${item.name}"`);
      await loadItems();
      await refreshTaskOptions(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // Drag-and-drop reordering (@dnd-kit). Optimistically updates the local
  // items list, then persists via /admin/options/{list_type}/reorder. On
  // failure we roll back by re-loading from the server.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = async (event) => {
    const { active: dActive, over } = event;
    if (!over || dActive.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === dActive.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(items, oldIndex, newIndex).map((it, idx) => ({ ...it, order: idx }));
    setItems(reordered);
    try {
      await axios.post(
        `${API}/task-options/admin/options/${activeTab}/reorder`,
        { ordered_ids: reordered.map((i) => i.id) },
        authHeader,
      );
      await refreshTaskOptions(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save new order');
      await loadItems();
    }
  };

  const filtered = items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));
  const stats = {
    total: items.length,
    active: items.filter((i) => i.is_active).length,
    archived: items.filter((i) => !i.is_active).length,
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="task-options-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
            <ListChecks size={20} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Task Options</h1>
            <p className="text-sm text-slate-500 mt-1">
              Single source of truth for every task dropdown, filter and workflow value. Changes propagate instantly across the Task Table, Kanban, Reports and My Day views.
            </p>
          </div>
        </div>
        <Button
          onClick={openCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="task-options-add-btn"
        >
          <Plus size={16} className="mr-2" />
          Add {active.singular}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-slate-200">
        {LIST_TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => { setActiveTab(t.key); setSearch(''); }}
            data-testid={`task-options-tab-${t.key}`}
            className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              activeTab === t.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300'
            }`}
          >
            {t.label}
            {t.hasCanonicalKey && <Lock size={11} className="opacity-70" />}
          </button>
        ))}
      </div>

      <p className="text-sm text-slate-600 mb-4">
        {active.description}
        {active.hasCanonicalKey && (
          <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
            <Lock size={10} /> canonical_key locked
          </span>
        )}
      </p>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="task-options-stat-total">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{stats.active}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Archived</p>
          <p className="text-2xl font-bold text-slate-400 mt-1">{stats.archived}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg p-4 border border-slate-200 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={`Search ${active.label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="task-options-search"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-slate-500">
            <Loader2 className="animate-spin mr-2" size={18} />
            Loading {active.label.toLowerCase()}...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="task-options-empty">
            No {active.label.toLowerCase()} found. Click <strong>Add</strong> to create one.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="pl-4 pr-2 py-3 w-10"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide w-20">Order</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Name</th>
                {active.hasCanonicalKey && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Canonical Key</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Description</th>
                {active.supportsColor && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Color</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={filtered.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {filtered.map((item) => (
                    <SortableOptionRow
                      key={item.id}
                      item={item}
                      active={active}
                      onEdit={openEdit}
                      onToggle={toggleActive}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? `Edit ${active.singular}` : `Add ${active.singular}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="task-option-name">Name<span className="text-red-500 ml-0.5">*</span></Label>
              <Input
                id="task-option-name"
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={80}
                placeholder="e.g. In Review"
                data-testid="task-option-name-input"
              />
            </div>
            {editingItem?.canonical_key && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5">
                  <Lock size={11} /> Canonical Key (read-only)
                </Label>
                <Input value={editingItem.canonical_key} disabled className="bg-slate-50 font-mono text-xs" />
                <p className="text-xs text-slate-500">
                  This is the immutable identifier the app uses internally. Rename the display name freely — filtering and reports remain intact.
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="task-option-desc">Description</Label>
              <Textarea
                id="task-option-desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional context or usage note"
                rows={2}
                data-testid="task-option-desc-input"
              />
            </div>
            {active.supportsColor && (
              <div className="space-y-1.5">
                <Label htmlFor="task-option-color">Color (hex)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="task-option-color"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    placeholder="#3B82F6"
                    className="font-mono"
                    maxLength={9}
                    data-testid="task-option-color-input"
                  />
                  {form.color && (
                    <span
                      className="w-9 h-9 rounded border border-slate-300 flex-shrink-0"
                      style={{ backgroundColor: form.color }}
                    />
                  )}
                </div>
                <p className="text-xs text-slate-500">Used to colour-code pills across the Task UI (statuses, priorities, tags).</p>
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <input
                id="task-option-active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="rounded"
                data-testid="task-option-active-input"
              />
              <Label htmlFor="task-option-active" className="font-normal cursor-pointer select-none">
                Active — show in dropdowns
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="task-option-save-btn"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : editingItem ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskOptions;
